import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ReactSelect from "react-select";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// === Styled Components ===
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background-color: #fff;
  font-size: 14px;
`;

// react-select styles to blend in
const comboStyles = {
  container: (base) => ({ ...base, minWidth: 260 }),
  menu: (base) => ({ ...base, zIndex: 5 }),
  control: (base) => ({
    ...base,
    borderRadius: 8,
    minHeight: 40,
    borderColor: "#ccc",
    boxShadow: "none",
    ":hover": { borderColor: "#aaa" },
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
  placeholder: (base) => ({ ...base, color: "#777" }),
};

const monthLabels = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// “one value per row” logic
const getRowValue = (row) =>
  parseFloat(row.K_15) ||
  parseFloat(row.K_19) ||
  parseFloat(row.K_10) ||
  parseFloat(row.K_21) ||
  parseFloat(row.K_17) ||
  parseFloat(row.K_11) ||
  parseFloat(row.K_22) ||
  0;

const ContractorActivityTrend = ({ data = [] }) => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");

  // contractor list for the selected year
  const [contractors, setContractors] = useState([]);

  // three picked contractors
  const [selectedContractors, setSelectedContractors] = useState({
    contractor1: "",
    contractor2: "",
    contractor3: "",
  });

  const [chartData, setChartData] = useState(null);

  // build year list & default to latest
  useEffect(() => {
    if (!data.length) return;

    const extractedYears = [
      ...new Set(
        data.map((row) => {
          const d = new Date(row.DataWystawienia);
          return !isNaN(d) ? d.getFullYear() : parseInt(row.Rok, 10);
        })
      ),
    ]
      .filter(Boolean)
      .sort((a, b) => a - b);

    setYears(extractedYears);
    if (!selectedYear && extractedYears.length) {
      setSelectedYear(String(extractedYears[extractedYears.length - 1]));
    }
  }, [data, selectedYear]);

  // filter rows by selected year
  const filteredYearData = useMemo(() => {
    if (!data.length || !selectedYear) return [];
    return data.filter((row) => {
      const d = new Date(row.DataWystawienia);
      const y = !isNaN(d) ? d.getFullYear() : parseInt(row.Rok, 10);
      return String(y) === String(selectedYear);
    });
  }, [data, selectedYear]);

  // build contractor list for that year, set defaults (top 3)
  useEffect(() => {
    if (!filteredYearData.length) {
      setContractors([]);
      setChartData(null);
      return;
    }

    const totals = filteredYearData.reduce((acc, row) => {
      const name = row.NazwaKontrahenta || "Nieznany kontrahent";
      acc[name] = (acc[name] || 0) + getRowValue(row);
      return acc;
    }, {});

    const sortedNames = Object.entries(totals)
      .sort(([, a], [, b]) => b - a)
      .map(([name]) => name);

    setContractors(sortedNames);

    setSelectedContractors((prev) => ({
      contractor1: prev.contractor1 || sortedNames[0] || "",
      contractor2: prev.contractor2 || sortedNames[1] || "",
      contractor3: prev.contractor3 || sortedNames[2] || "",
    }));
  }, [filteredYearData]);

  // options for react-select
  const contractorOptions = useMemo(
    () => contractors.map((c) => ({ value: c, label: c })),
    [contractors]
  );

  const selectedOption = (key) => {
    const val = selectedContractors[key];
    return val ? { value: val, label: val } : null;
    };

  // build chart for selected contractors (12 months)
  useEffect(() => {
    const picked = Object.values(selectedContractors).filter(Boolean);
    if (!filteredYearData.length || picked.length === 0) {
      setChartData(null);
      return;
    }

    const aggregated = filteredYearData.reduce((acc, row) => {
      const name = row.NazwaKontrahenta || "Nieznany kontrahent";
      if (!picked.includes(name)) return acc;

      let m = parseInt(row.Miesiac, 10) - 1;
      if (isNaN(m)) {
        const d = new Date(row.DataWystawienia);
        m = !isNaN(d) ? d.getMonth() : -1;
      }
      if (m < 0 || m > 11) return acc;

      const val = getRowValue(row);
      if (!acc[name]) acc[name] = Array(12).fill(0);
      acc[name][m] += val;
      return acc;
    }, {});

    // 3 distinct lines (using your preferred palette family)
    const palette = ["#2e7d32", "#fb8c00", "#2196f3"]; // green, orange, blue
    const datasets = Object.keys(aggregated).map((c, i) => ({
      label: c,
      data: aggregated[c],
      borderColor: palette[i % palette.length],
      backgroundColor: "rgba(0,0,0,0)",
      tension: 0.35,
      pointRadius: 3,
      borderWidth: 2,
    }));

    setChartData({ labels: monthLabels, datasets });
  }, [filteredYearData, selectedContractors]);

  const handleContractorChange = (key, option) => {
    setSelectedContractors((prev) => ({ ...prev, [key]: option?.value || "" }));
  };

  return (
    <ChartWrapper>
      <ChartTitle>
        Aktywność kontrahentów w czasie
        <InfoIconWrapper data-tooltip-id="contractor-trend-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="contractor-trend-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Aktywność kontrahentów</strong><br/>
            Wykres pokazuje zmiany sprzedaży (miesięcznie) dla wybranych kontrahentów w wybranym roku.
          </>
        }
      />

      {/* Year selector */}
      <FilterWrapper>
        <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
      </FilterWrapper>

      {/* Three searchable contractor dropdowns (inline search) */}
      <FilterWrapper>
        {["contractor1", "contractor2", "contractor3"].map((key, i) => (
          <ReactSelect
            key={key}
            styles={comboStyles}
            options={contractorOptions}
            value={selectedOption(key)}
            onChange={(opt) => handleContractorChange(key, opt)}
            isSearchable
            placeholder={`Wybierz kontrahenta ${i + 1}`}
            noOptionsMessage={() => "Brak wyników"}
          />
        ))}
      </FilterWrapper>

      {chartData ? (
        <Line
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.raw)}`,
                },
              },
            },
            scales: {
              x: { title: { display: true, text: "Miesiące" } },
              y: {
                beginAtZero: true,
                title: { display: true, text: "Wartość sprzedaży" },
                ticks: { callback: (v) => formatNumber(v) },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>Wybierz rok i kontrahentów, aby zobaczyć dane</p>
      )}
    </ChartWrapper>
  );
};

export default ContractorActivityTrend;
